import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import Link from '../../../../Link/viewer/Link';
import { testIds } from '../../testIds';
import { getIsCurrentPage } from '../../utils/getIsCurrentPage';
import { IMenuItemLabelProps } from '../../../StylableHorizontalMenu.types';
import { getLabelClasses } from './styles/getLabelClasses';

const createEventListeners = (setIsHovered: (value: boolean) => void) => {
  const onEnter = () => setIsHovered(true);
  const onLeave = () => setIsHovered(false);
  return {
    onMouseEnter: onEnter,
    onMouseLeave: onLeave,
    onFocusCapture: onEnter,
    onBlurCapture: onLeave,
  };
};

export const MenuItemLabel: React.FC<IMenuItemLabelProps> = ({
  item,
  className,
  withSubItemsClassname,
  currentPageHref,
  depth,
  hasSubSubs,
  positionUpdaters: positionUpdatersMap,
  positionBoxRef,
  children,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const labelRef = useRef<HTMLAnchorElement | HTMLDivElement | null>(null);

  const { label, link, forceHovered = false } = item;
  const eventListeners = createEventListeners(setIsHovered);
  const isCurrentPage = getIsCurrentPage(link, currentPageHref);
  const classes = getLabelClasses({
    depth,
    isHovered,
    isCurrentPage,
    className,
  });

  const positionUpdaters = positionUpdatersMap[depth];
  useEffect(() => {
    if (
      !isHovered ||
      !labelRef.current ||
      !positionBoxRef.current ||
      !positionUpdaters
    ) {
      return;
    }

    const { onEnter, onLeave } = positionUpdaters({
      label: labelRef.current,
      positionBox: positionBoxRef.current,
    });
    onEnter();
    return onLeave;
  }, [isHovered, positionBoxRef, positionUpdaters]);

  useEffect(() => {
    setIsHovered(forceHovered);
  }, [forceHovered]);

  // heading should listen events only inside label
  const isHeading = hasSubSubs && depth === 1;

  return (
    <li
      className={cn(classes.itemWrapper, withSubItemsClassname)}
      data-testid={testIds.menuItem(depth)}
      data-item-depth={depth}
      data-is-current={isCurrentPage}
      {...(isHovered && {
        'data-hovered': true,
      })}
      {...(!isHeading && eventListeners)}
    >
      <Link
        {...link}
        {...(isHeading && eventListeners)}
        className={classes.root}
        ref={labelRef}
      >
        <div className={classes.container}>
          <span className={classes.label}>{label}</span>
        </div>
      </Link>
      {children}
    </li>
  );
};
